import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveApiErrorMessage } from '@/api/resolveApiErrorMessage';
import type { TreatmentPlanTemplate } from '@/api/treatmentPlanTemplates/treatmentPlanTemplatesApi.types';
import { useReplaceTreatmentPlanTemplatesMutation } from '@/hooks/useTreatmentPlanTemplates';
import { useTreatmentsQuery } from '@/hooks/useTreatmentLibraryQueries';
import type {
  EditorPlanExercise,
  EditorPlanItem,
} from '@/components/practitioner/TreatmentPlanEditor/types';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Icon } from '@/components/common/Icon';
import { FieldError } from '@/components/ui/fieldError';
import { isBlank } from '@/utils/formValidation';
import { showErrorNotification, showSuccessNotification } from '@/utils/notifications';
import { PlanTemplateCard } from './PlanTemplateCard';

export interface EditableTemplate {
  key: string;
  code: string | null;
  nameEn: string;
  namePl: string;
  items: EditorPlanItem[];
  exercises: EditorPlanExercise[];
}

let templateKeyCounter = 0;
function nextTemplateKey(): string {
  templateKeyCounter = templateKeyCounter + 1;
  return 'plan-template-' + templateKeyCounter;
}

function toEditableTemplates(templates: TreatmentPlanTemplate[]): EditableTemplate[] {
  return templates.map(function toEditable(template) {
    return {
      key: nextTemplateKey(),
      code: template.code,
      nameEn: template.nameEn,
      namePl: template.namePl,
      items: template.items.map(function toEditorItem(item) {
        return {
          key: nextTemplateKey(),
          treatmentId: item.treatmentId,
          treatmentName: item.treatmentName,
          instructions: item.instructions === null ? '' : item.instructions,
          progress: null,
        };
      }),
      exercises: template.exercises.map(function toEditorExercise(exercise) {
        return {
          key: nextTemplateKey(),
          name: exercise.name,
          instructions: exercise.instructions === null ? '' : exercise.instructions,
          frequency: exercise.frequency === null ? '' : exercise.frequency,
          compliance: null,
        };
      }),
    };
  });
}

interface PlanTemplatesEditorProps {
  initialTemplates: TreatmentPlanTemplate[];
}

export function PlanTemplatesEditor({ initialTemplates }: PlanTemplatesEditorProps) {
  const { t } = useTranslation('plans');
  const treatmentsQuery = useTreatmentsQuery('', null, false, true);
  const mutation = useReplaceTreatmentPlanTemplatesMutation();
  const [templates, setTemplates] = useState<EditableTemplate[]>(
    toEditableTemplates(initialTemplates)
  );
  const [isSaving, setIsSaving] = useState(false);
  const [hasIncompleteNames, setHasIncompleteNames] = useState(false);

  useEffect(
    function syncFromServer() {
      setTemplates(toEditableTemplates(initialTemplates));
      setHasIncompleteNames(false);
    },
    [initialTemplates]
  );

  const libraryTreatments = treatmentsQuery.data === undefined ? [] : treatmentsQuery.data;

  function addTemplate() {
    setTemplates(function append(current) {
      return [
        ...current,
        { key: nextTemplateKey(), code: null, nameEn: '', namePl: '', items: [], exercises: [] },
      ];
    });
  }

  function removeTemplate(key: string) {
    setTemplates(function apply(current) {
      return current.filter(function keep(template) {
        return template.key !== key;
      });
    });
  }

  function updateTemplate(updated: EditableTemplate) {
    setHasIncompleteNames(false);
    setTemplates(function apply(current) {
      return current.map(function replace(existing) {
        return existing.key === updated.key ? updated : existing;
      });
    });
  }

  async function save() {
    const hasIncomplete = templates.some(function incomplete(template) {
      return isBlank(template.nameEn) || isBlank(template.namePl);
    });
    if (hasIncomplete) {
      setHasIncompleteNames(true);
      showErrorNotification(t('templates.incompleteTemplate'));
      return;
    }

    const drafts = templates.map(function toDraft(template) {
      return {
        code: template.code,
        nameEn: template.nameEn.trim(),
        namePl: template.namePl.trim(),
        items: template.items.map(function toItemDraft(item, index) {
          return {
            treatmentId: item.treatmentId,
            instructions: item.instructions.trim() === '' ? null : item.instructions,
            sortOrder: index,
          };
        }),
        exercises: template.exercises
          .filter(function hasName(exercise) {
            return exercise.name.trim().length > 0;
          })
          .map(function toExerciseDraft(exercise, index) {
            return {
              name: exercise.name.trim(),
              instructions: exercise.instructions.trim() === '' ? null : exercise.instructions,
              frequency: exercise.frequency.trim() === '' ? null : exercise.frequency,
              sortOrder: index,
            };
          }),
      };
    });

    setIsSaving(true);
    try {
      const saved = await mutation.mutateAsync(drafts);
      setTemplates(toEditableTemplates(saved));
      setHasIncompleteNames(false);
      showSuccessNotification(t('templates.saved'));
    } catch (error) {
      const message = resolveApiErrorMessage(error);
      showErrorNotification(message.length > 0 ? message : t('templates.saveError'));
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Card padding="md">
      <h2 className="text-sm font-bold text-zooindigo mb-4">{t('templates.settingsTitle')}</h2>

      {templates.length === 0 ? (
        <p className="text-sm text-zoomuted mb-3">{t('templates.empty')}</p>
      ) : (
        <div className="space-y-4 mb-4">
          {templates.map(function renderTemplate(template) {
            return (
              <PlanTemplateCard
                key={template.key}
                template={template}
                libraryTreatments={libraryTreatments}
                isDisabled={isSaving}
                hasIncompleteName={hasIncompleteNames}
                onChange={updateTemplate}
                onRemove={function remove() {
                  removeTemplate(template.key);
                }}
              />
            );
          })}
        </div>
      )}

      {hasIncompleteNames ? <FieldError messageKey="plans:templates.incompleteTemplate" /> : null}

      <div className="flex items-center justify-between mt-4">
        <Button type="button" variant="outline" size="small" disabled={isSaving} onClick={addTemplate}>
          <Icon name="Plus" /> {t('templates.addTemplate')}
        </Button>
        <Button type="button" isLoading={isSaving} onClick={save}>
          {t('templates.save')}
        </Button>
      </div>
    </Card>
  );
}
